import React, { useEffect, useState, useRef } from 'react';
import {
  Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, MenuItem, TextField, Alert, LinearProgress, Box, Typography,
} from '@mui/material';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { useAuth } from '../../contexts/AuthContext';
import { getCameraUploadFolderImages } from '../../api/cameraUpload';
import { listAnnotatorProjects, pushImageToTraining } from '../../api/cameraUploadTrainingPush';
import type { AnnotatorProject } from '../../types/annotator';

const FETCH_PAGE_SIZE = 200;

interface PushFolderToTrainingDataButtonProps {
  deviceId: string;
  className: string;
  imageCount: number;
}

// Bulk version of PushToTrainingDataButton — pushes every image in the
// folder that has at least one boxed classification, one at a time (each
// push is itself a blob fetch + Annotator upload + annotation PUT, so this
// can take a while for a large folder).
const PushFolderToTrainingDataButton: React.FC<PushFolderToTrainingDataButtonProps> = ({ deviceId, className, imageCount }) => {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<AnnotatorProject[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [pushedCount, setPushedCount] = useState(0);
  const [skippedNoBox, setSkippedNoBox] = useState<string[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setLoadingProjects(true);
    setError('');
    setDone(false);
    setProcessed(0);
    setTotal(0);
    setPushedCount(0);
    setSkippedNoBox([]);
    setFailed([]);
    listAnnotatorProjects()
      .then((data) => {
        setProjects(data);
        if (data.length > 0) setProjectId(data[0].id);
      })
      .catch((err: any) => setError(err?.message || 'Failed to load Annotator projects'))
      .finally(() => setLoadingProjects(false));
  }, [open]);

  if (!auth.hasRole('DMO_ADMIN')) return null;

  const handlePush = async () => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const accessToken = auth.tokens?.accessToken ?? '';
    cancelledRef.current = false;
    setRunning(true);
    setError('');
    setPushedCount(0);
    setSkippedNoBox([]);
    setFailed([]);

    try {
      let page = 1;
      let totalCount = Infinity;
      let seen = 0;
      const pushed = [];
      const skipped: string[] = [];
      const failures: string[] = [];

      while (seen < totalCount && !cancelledRef.current) {
        const result = await getCameraUploadFolderImages(deviceId, className, page, FETCH_PAGE_SIZE, accessToken);
        totalCount = result.totalCount;
        setTotal(totalCount);

        for (const image of result.images) {
          if (cancelledRef.current) break;
          const hasBox = image.classifications.some((c) => c.boxX1 != null && c.boxY1 != null && c.boxX2 != null && c.boxY2 != null);
          if (!hasBox) {
            skipped.push(image.originalFileName);
          } else {
            try {
              await pushImageToTraining(deviceId, image, project, accessToken);
              pushed.push(image.originalFileName);
            } catch {
              failures.push(image.originalFileName);
            }
          }
          seen += 1;
          setProcessed(seen);
          setPushedCount(pushed.length);
          setSkippedNoBox([...skipped]);
          setFailed([...failures]);
        }
        page += 1;
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to push folder to training data');
    } finally {
      setRunning(false);
      setDone(true);
    }
  };

  const handleClose = () => {
    if (running) cancelledRef.current = true;
    else setOpen(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ModelTrainingIcon />}
        onClick={() => setOpen(true)}
        disabled={imageCount === 0}
      >
        Add folder to training data
      </Button>

      <Dialog open={open} onClose={running ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Push "{className}" folder to Annotator training data</DialogTitle>
        <DialogContent>
          {!running && !done && (
            <DialogContentText sx={{ mb: 2 }}>
              Uploads every image in this folder ({imageCount}) that has at least one boxed classification into an
              Annotator project. Images with no drawn box are skipped.
            </DialogContentText>
          )}

          {!done && (
            loadingProjects ? (
              <CircularProgress size={24} />
            ) : (
              <TextField
                select
                label="Annotator project"
                value={projectId}
                onChange={(e) => setProjectId(Number(e.target.value))}
                fullWidth
                size="small"
                disabled={running}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            )
          )}

          {(running || done) && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={total > 0 ? (processed / total) * 100 : 0} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {processed} / {total || '?'} processed — {pushedCount} pushed, {skippedNoBox.length} skipped (no box), {failed.length} failed
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {done && (
            <Alert severity={failed.length > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {cancelledRef.current ? 'Cancelled. ' : 'Done. '}
              Pushed {pushedCount}, skipped {skippedNoBox.length} with no box{failed.length > 0 ? `, failed ${failed.length}` : ''}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{running ? 'Cancel' : 'Close'}</Button>
          {!done && (
            <Button variant="contained" onClick={handlePush} disabled={running || !projectId}>
              {running ? <CircularProgress size={20} /> : 'Push'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PushFolderToTrainingDataButton;
